import type { ProductDto, ProductVariantDto } from "../../../api";
import { getProductUnitPrice } from "./get-pricelist-price";

export function computeDiscount(product: ProductDto): number | null {
  const { listPrice, standardPrice } = product;
  if (!standardPrice || !listPrice || standardPrice <= listPrice) return null;
  return Math.round((1 - listPrice / standardPrice) * 100);
}

export function getVariantDiscount(variant: ProductVariantDto): number | null {
  const discount = variant.discountPercentage;
  return typeof discount === "number" && discount > 0 ? discount : null;
}

/**
 * The variant's buyer-facing price for a single unit — the same one the
 * product page and cart charge (qty-1 pricelist tier, else list price + extra)
 * — or `null` when it has none. A zero price means it isn't set yet, not that
 * the product is free, so it must never read as "QAR 0" or win a comparison.
 */
export function getVariantUnitPrice(variant: ProductVariantDto): number | null {
  const price = getProductUnitPrice(variant, 1);
  return Number.isFinite(price) && price > 0 ? price : null;
}

export function getVariantOriginalPrice(variant: ProductVariantDto): number | null {
  const discount = getVariantDiscount(variant);
  if (!discount) return null;
  return variant.standardPrice ?? null;
}

/**
 * The id the wishlist store keys a catalog product by. Likes are stored per
 * `product.product` variant, so this must be `firstVariantId` — `product.id` is
 * the template id and `product.variantId` is an attribute-value id, and
 * passing either one silently likes an unrelated variant. Returns `null` when
 * the variant isn't known, so callers hide the heart instead of guessing.
 */
export function getWishlistVariantId(product: ProductDto): number | null {
  return product.firstVariantId ?? null;
}

export function getStockLabel(product: ProductDto): string | undefined {
  return product.id % 5 === 0 ? "Limited Stock" : undefined;
}
